import React, { useState, useEffect, useContext } from "react";
import { Edit2, Users, ShoppingCart, Trash2, Plus, Minus, ArrowRight, Check, Printer, X, Tag } from "lucide-react";
import ModernDropdown from './ModernDropdown';
import { useNavigate } from "react-router-dom";
import { NotificationContext } from '../../context/NotificationContext';
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";

export default function CartSidebar({
    cart,
    customer,
    onSelectCustomer,
    onRemoveCustomer,
    onUpdateQty,
    onUpdateDiscount,
    onRemoveItem,
    subtotal,
    discount, // Added prop
    tax,
    total,
    onPlaceOrder,
    billingType,
    setBillingType,
    paymentMode,
    gstEnabled,
    setGstEnabled,
    taxMode,
    setTaxMode,
    taxRate,
    invoiceNo,
    onClearCart,
    onProceedToPayment
}) {
    const navigate = useNavigate();
    const { addNotification } = useContext(NotificationContext);

    const [editingItemDiscount, setEditingItemDiscount] = useState(null);

    const handleProceed = () => {
        if (cart.length === 0) {
            addNotification("error", "Cart is Empty", "Please add items to the cart before proceeding.");
            return;
        }
        if (onProceedToPayment) {
            onProceedToPayment();
        } else {
            // Fallback for safety
            navigate('/pos/payment', {
                state: {
                    total: total,
                    items: cart,
                    customer: customer,
                    invoiceNo: invoiceNo
                }
            });
        }
    };

    const handleItemDiscountUpdate = (id, value) => {
        const val = parseFloat(value);
        if (!isNaN(val) && val >= 0) {
            onUpdateDiscount(id, val);
        }
        setEditingItemDiscount(null);
    };

    return (
        <div className="flex flex-col h-full bg-white relative z-20 font-sans border-l border-gray-100/50">
            {/* Header Section */}
            <div className="px-8 py-8 border-b border-gray-100 flex flex-col gap-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Current Order</h2>
                        <div className="mt-2 flex items-center">
                            <div className="flex items-center bg-green-50 text-green-700 px-3 py-1.5 rounded-lg border border-green-100">
                                <span className="text-[10px] font-bold uppercase tracking-widest mr-2 opacity-60">Inv</span>
                                <span className="text-xs font-black font-mono tracking-wider">
                                    {invoiceNo || "NEW-ORDER"}
                                </span>
                            </div>
                        </div>
                    </div>
                    {cart.length > 0 && (
                        <button
                            onClick={onClearCart}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                            title="Clear Order"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Controls Container */}
                <div className="space-y-3">
                    <ModernDropdown
                        value={billingType}
                        onChange={(val) => setBillingType(val)}
                        options={[
                            { value: "Walk-in Bill", label: "Walk-in Bill" },
                            { value: "Customer Bill", label: "Customer Bill" },
                            { value: "Credit / Pending Bill", label: "Credit / Pending Bill" },
                            { value: "GST Bill", label: "GST Bill" },
                            { value: "Non-GST Bill", label: "Non-GST Bill" },
                        ]}
                    />

                    <div
                        onClick={!customer ? onSelectCustomer : undefined}
                        className={`
                            group w-full p-2.5 rounded-xl border-2 transition-all cursor-pointer relative
                            ${customer
                                ? 'bg-green-50 border-green-100 hover:border-green-200'
                                : 'bg-gray-50 border-transparent hover:border-gray-200'}
                        `}
                    >
                        {customer ? (
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 overflow-hidden" onClick={onSelectCustomer}>
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-green-600 font-bold border border-gray-200 shadow-sm shrink-0">
                                        {customer.name?.[0]?.toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-gray-900 truncate">{customer.name}</p>
                                        <p className="text-xs text-gray-500 font-medium">{customer.phone}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={(e) => { e.stopPropagation(); onSelectCustomer() }} className="p-2 text-gray-400 hover:text-green-500 hover:bg-white rounded-lg transition-all"><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={(e) => { e.stopPropagation(); onRemoveCustomer() }} className="p-2 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg transition-all"><X className="w-4 h-4" /></button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center gap-2 py-2 text-gray-400 group-hover:text-gray-600 transition-all font-bold text-sm">
                                <Users className="w-4 h-4" />
                                <span>Find Customer</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Cart Items List */}
            <div className="flex-1 overflow-y-auto px-8 py-4 divide-y divide-gray-100 flex flex-col gap-0 bg-white">
                {cart.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center py-20 grayscale opacity-50">
                        <ShoppingCart className="w-12 h-12 text-gray-300" />
                        <p className="mt-4 font-bold text-gray-400">Cart is Empty</p>
                    </div>
                ) : (
                    cart.map((item, idx) => (
                        <div key={idx} className="py-6 flex gap-4 group items-start group">
                            {/* Simple Image */}
                            <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800/50 flex-shrink-0 overflow-hidden border border-gray-200 dark:border-gray-700 relative shadow-sm">
                                {item.image ? (
                                    <img 
                                        src={resolveMediaUrl(item.image)}
                                        alt={item.name} 
                                        className="w-full h-full object-cover" 
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-xl opacity-50">☕</div>
                                )}
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start gap-2">
                                    <h4 className="font-bold text-gray-800 text-sm leading-snug line-clamp-2">
                                        {item.name} {item.variant_name && <span className="text-blue-600 font-medium ml-1">({item.variant_name})</span>}
                                        {item.is_gift && (
                                            <span className="ml-2 bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-[8px] uppercase font-black border border-purple-200">
                                                Gift
                                            </span>
                                        )}
                                    </h4>
                                    <span className="font-bold text-gray-900 text-sm">
                                        {item.is_gift ? (
                                            <div className="flex flex-col items-end">
                                                <span className="text-gray-300 line-through text-[10px]">₹{(item.original_price * item.qty).toLocaleString()}</span>
                                                <span className="text-purple-600">₹0</span>
                                            </div>
                                        ) : (
                                            `₹${((item.price * item.qty) - (item.discount || 0)).toLocaleString()}`
                                        )}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between mt-2">
                                    <div className="flex flex-col">
                                        <span className="text-[11px] text-gray-400 font-medium font-mono tracking-tight">
                                        {item.is_gift ? (
                                            `Gift (Free) × ${parseFloat(item.qty)} ${item.unit || 'pc'}`
                                        ) : (
                                            item.product_type === 'weight_based' 
                                                ? `${parseFloat(item.qty)} kg × ₹${item.price.toLocaleString()}`
                                                : `₹${item.price.toLocaleString()} / ${item.unit || 'pc'}`
                                        )}
                                        </span>
                                        {item.discount > 0 && (
                                            <span className="text-[10px] text-green-600 font-bold mt-0.5">
                                                Saved ₹{item.discount}
                                            </span>
                                        )}
                                    </div>

                                    {/* Edit/Trash Actions */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => onUpdateQty((item.cartItemId || item.id), 0)}
                                            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                            title="Remove Item"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>

                                        {/* Clean Qty Controls */}
                                        <div className="flex items-center bg-gray-50 rounded-lg p-0.5 border border-gray-200 shadow-sm">
                                            <button
                                                onClick={() => onUpdateQty((item.cartItemId || item.id), Math.max(0, (parseFloat(item.qty) || 0) - (item.product_type === 'weight_based' ? 0.25 : 1)))}
                                                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-white transition-all"
                                            >
                                                <Minus className="w-3.5 h-3.5" />
                                            </button>
                                            <input
                                                type="number"
                                                step={item.product_type === 'weight_based' ? "0.01" : (['piece', 'pcs', 'number', 'unit'].includes((item.unit || "").toLowerCase()) ? "1" : "any")}
                                                value={item.qty}
                                                onChange={(e) => onUpdateQty((item.cartItemId || item.id), e.target.value)}
                                                className="text-[10px] font-bold w-12 text-center text-gray-900 bg-transparent outline-none"
                                            />
                                            <button
                                                onClick={() => onUpdateQty((item.cartItemId || item.id), (parseFloat(item.qty) || 0) + (item.product_type === 'weight_based' ? 0.25 : 1))}
                                                className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-green-600 hover:bg-white transition-all"
                                            >
                                                <Plus className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {editingItemDiscount === (item.cartItemId || item.id) ? (
                                    <div className="mt-2 flex gap-2">
                                        <input
                                            type="number"
                                            autoFocus
                                            defaultValue={item.discount || ""}
                                            placeholder="Disc ₹"
                                            className="flex-1 p-2 text-xs font-bold border border-green-200 rounded-lg outline-none focus:ring-2 focus:ring-green-500 transition-all shadow-sm"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleItemDiscountUpdate((item.cartItemId || item.id), e.currentTarget.value);
                                                if (e.key === 'Escape') setEditingItemDiscount(null);
                                            }}
                                            onBlur={(e) => handleItemDiscountUpdate((item.cartItemId || item.id), e.target.value)}
                                        />
                                        <button 
                                            className="p-2 bg-green-50 text-green-600 rounded-lg border border-green-100"
                                            onClick={() => setEditingItemDiscount(null)}
                                        >
                                            <Check className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="mt-2 flex items-center gap-2">
                                        <button
                                            onClick={() => setEditingItemDiscount((item.cartItemId || item.id))}
                                            className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider transition-all px-2.5 py-1.5 rounded-lg
                                                ${item.discount > 0 
                                                    ? 'text-green-600 bg-green-50 border border-green-100 shadow-sm' 
                                                    : 'text-gray-400 hover:text-green-600 hover:bg-green-50/50 border border-transparent hover:border-green-100'}`}
                                        >
                                            <Tag className="w-3 h-3" />
                                            {item.discount ? `Disc: ₹${item.discount}` : 'Add Discount'}
                                        </button>
                                        
                                        {item.discount > 0 && (
                                            <button
                                                onClick={() => onUpdateDiscount((item.cartItemId || item.id), 0)}
                                                className="p-1.5 text-red-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
                                                title="Remove Discount"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Simple Clean Footer */}
            <div className="p-8 bg-gray-50/50 border-t border-gray-100">
                <div className="space-y-2 mb-8">
                    <div className="flex justify-between text-xs font-medium text-gray-500">
                        <span>Items Subtotal</span>
                        <span className="text-gray-900 font-bold">₹{subtotal.toFixed(0)}</span>
                    </div>
                    {discount > 0 && (
                        <div className="flex justify-between text-xs font-medium text-green-600 bg-green-50 p-1.5 rounded-lg -mx-1">
                            <span className="font-bold uppercase tracking-wider text-[10px]">Applied Discount</span>
                            <span className="font-bold">-₹{discount.toFixed(0)}</span>
                        </div>
                    )}
                    <div className="flex justify-between text-xs font-medium text-gray-500">
                        <span className="font-bold uppercase tracking-wider text-[10px]">
                            CGST {taxMode === 'inclusive' && <span className="text-blue-500 lowercase">(Incl.)</span>}
                        </span>
                        <span className="text-gray-900 font-bold">₹{(tax / 2).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-medium text-gray-500">
                        <span className="font-bold uppercase tracking-wider text-[10px]">
                            SGST {taxMode === 'inclusive' && <span className="text-blue-500 lowercase">(Incl.)</span>}
                        </span>
                        <span className="text-gray-900 font-bold">₹{(tax / 2).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-5 mt-4 border-t border-gray-200">
                        <span className="text-base font-bold text-gray-900">Total Payable</span>
                        <span className="text-3xl font-black text-gray-900 leading-none tracking-tight">
                            <span className="text-lg mr-1 opacity-50 font-medium">₹</span>
                            {total.toLocaleString()}
                        </span>
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={onClearCart}
                        className="p-4 bg-white border border-gray-200 text-gray-400 rounded-2xl hover:text-red-500 hover:border-red-100 transition-all flex items-center justify-center shrink-0"
                    >
                        <Trash2 className="w-6 h-6" />
                    </button>
                    <button
                        onClick={handleProceed}
                        className="flex-1 py-4 bg-green-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-green-100 hover:bg-green-700 hover:scale-[1.01] active:scale-95 transition-all flex items-center justify-center gap-3"
                    >
                        Proceed to Pay
                        <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </div>


        </div>
    );
}
